import { MouseEvent, ReactNode, useEffect } from 'react'
import cx from 'classnames'

import Styles from './Modal.module.scss'
import { ReactComponent as CloseIcon } from 'assets/icons/close24.svg'

export interface Props {
  animated?: boolean
  bodyClassName?: string
  children: ReactNode
  footerChildren?: ReactNode
  onClose: () => void
  onEnter?: () => void
  show: boolean
  title?: ReactNode
  bodyClassname?: string
  wrapperClassName?: string
  headerClassName?: string
  showCloseIcon?: boolean
  withGradient?: boolean
}

export const Modal = ({
  animated = true,
  bodyClassName,
  children,
  footerChildren,
  onClose,
  onEnter,
  show,
  title,
  headerClassName,
  wrapperClassName,
  showCloseIcon = true,
  withGradient = false,
}: Props) => {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
      if (event.key === 'Enter' && onEnter) {
        onEnter?.()
      }
    }

    window.addEventListener('keydown', onKeyDown, true)

    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [onClose, onEnter])

  const onClick = (event: MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose()
    }
  }

  return show ? (
    <div
      className={cx({ [Styles.animated]: animated }, Styles.backdrop, bodyClassName)}
      data-testid="modalBackdrop"
      onClick={onClick}
    >
      <div className={cx(Styles.modal, { [Styles.withGradient]: withGradient }, wrapperClassName)}>
        {(title || showCloseIcon) && (
          <header className={cx(headerClassName)}>
            {title}
            {showCloseIcon && (
              <button aria-label="Close modal" className={Styles.closeButton} onClick={onClose}>
                <CloseIcon />
              </button>
            )}
          </header>
        )}

        <div className={cx(Styles.modalBody, bodyClassName)}>
          {children}
          {footerChildren && (
            <>
              <hr className={Styles.footerDivider} />
              <footer className={Styles.modalFooter}>{footerChildren}</footer>
            </>
          )}
        </div>
      </div>
    </div>
  ) : null
}
